// @flow
import React, { useCallback, useState } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localDateTimeIso8601 from "@justpark/helpers/src/localDateTimeIso8601/localDateTimeIso8601";
import LocationSearchInput from "../LocationSearchInput";
import SearchFormDateInput from "../SearchFormDateInput";
import SearchDurationArrowIcon from "../Icons/SearchDurationArrowIcon";
import SearchFormTabs from "../SearchFormTabs";
import SearchFormMonthlyDurationInput from "../SearchFormMonthlyDurationInput";

import styles from "./searchForm.module.scss";

dayjs.extend(isSameOrBefore);

type Props = {
  query: string,
  coords: { lat: number, lng: number } | null,
  arriving: string,
  leaving: string,
  onChange: (any, any) => void,
  bookingType: "hourly" | "monthly",
  monthlyStarting: string,
  monthlyDuration: string,
  showValidation: boolean,
  searchInputRef: any,
  setDefaultHourlyDates: any,
  setDefaultMonthlyDates: any
};

const SearchForm = ({
  query,
  coords,
  arriving,
  leaving,
  onChange,
  bookingType,
  monthlyStarting,
  monthlyDuration,
  searchInputRef,
  showValidation,
  setDefaultHourlyDates,
  setDefaultMonthlyDates
}: Props) => {
  // This is a hack to force the date inputs to re-render. The date inputs have their own internal state, and we need to
  // ensure their state is reset when we modify the data in the validation function
  const [keyIncrement, setKeyIncrement] = useState(0);

  const validatedOnChange = useCallback(
    (newData) => {
      return onChange((existingData) => {
        const mergedData = {
          ...existingData,
          ...newData
        };

        const { arriving: newArriving, leaving: newLeaving } = mergedData;

        if (newArriving) {
          const arrivingDate = dayjs(newArriving);
          const leavingDate = dayjs(newLeaving);

          if (
            leavingDate.isValid() === false ||
            leavingDate.isSameOrBefore(arrivingDate)
          ) {
            mergedData.leaving = arrivingDate
              .add(4, "hours")
              .format(localDateTimeIso8601);

            setKeyIncrement((prev) => prev + 1);
          }
        }

        return mergedData;
      });
    },
    [onChange, setKeyIncrement]
  );

  const onLocationChange = useCallback(
    (value, searchData) => {
      validatedOnChange({
        query: value,
        searchData
      });
    },
    [validatedOnChange]
  );

  const onArrivingChange = useCallback(
    (value) => {
      validatedOnChange({
        arriving: value
      });
    },
    [validatedOnChange]
  );

  const onLeavingChange = useCallback(
    (value) => {
      validatedOnChange({
        leaving: value
      });
    },
    [validatedOnChange]
  );

  const onBookingTypeChange = useCallback(
    (value) => {
      validatedOnChange({
        bookingType: value
      });
    },
    [validatedOnChange]
  );

  const onMonthlyStartingChange = useCallback(
    (value) => {
      validatedOnChange({
        monthlyStarting: value
      });
    },
    [validatedOnChange]
  );

  const onMonthlyDurationChange = useCallback(
    (value) => {
      validatedOnChange({
        monthlyDuration: value
      });
    },
    [validatedOnChange]
  );

  return (
    <div>
      <div className={styles.tabContainer}>
        <SearchFormTabs active={bookingType} onChange={onBookingTypeChange} />
      </div>

      <LocationSearchInput
        value={query}
        onChange={onLocationChange}
        searchInputRef={searchInputRef}
        invalid={showValidation && !coords}
      />

      <hr className={styles.hr} />

      <div className={styles.durationContainer}>
        {bookingType === "hourly" ? (
          <SearchFormDateInput
            type="from"
            date={arriving}
            setDate={onArrivingChange}
            key={`arriving-${keyIncrement}`}
            blockPastDates
            invalid={showValidation && !arriving}
            setDefaultDates={setDefaultHourlyDates}
          />
        ) : (
          <SearchFormDateInput
            type="monthly"
            date={monthlyStarting}
            setDate={onMonthlyStartingChange}
            invalid={showValidation && !monthlyStarting}
            blockPastDates
            setDefaultDates={setDefaultMonthlyDates}
          />
        )}

        <div className={styles.durationIcon}>
          <SearchDurationArrowIcon />
        </div>

        {bookingType === "hourly" ? (
          <SearchFormDateInput
            type="to"
            date={leaving}
            setDate={onLeavingChange}
            key={`leaving-${keyIncrement}`}
            blockPastDates
            invalid={showValidation && !leaving}
            min={arriving}
            setDefaultDates={setDefaultHourlyDates}
          />
        ) : (
          <SearchFormMonthlyDurationInput
            selected={monthlyDuration}
            onChange={onMonthlyDurationChange}
          />
        )}
      </div>
    </div>
  );
};

export default SearchForm;
